import { useEffect } from 'react';
import { SITE_URL } from '../config/site';

interface SEOProps {
  title: string;
  description: string;
  name?: string;
  type?: string;
  image?: string;
  schema?: Record<string, any>;
  noindex?: boolean;
}

export default function SEO({ title, description, name = 'Reviio', type = 'website', image, schema, noindex = false }: SEOProps) {
  // Guard: detect raw i18n keys that failed to resolve (e.g. "seo.blogList.title")
  // Pattern: lowercase word, then dots and camelCase segments — never a real title
  const isRawKey = (s: string) => /^[a-z]+\.[a-zA-Z]+(\.[a-zA-Z]+)*$/.test(s);

  const safeTitle = isRawKey(title) ? 'Reviio' : title;
  const safeDescription = isRawKey(description)
    ? 'AI-powered Google & Facebook review management. Respond faster, boost your online reputation.'
    : description;

  if (import.meta.env.DEV && (isRawKey(title) || isRawKey(description))) {
    console.warn(`[SEO] Raw i18n key detected! title="${title}" description="${description}". Add missing keys to locale files.`);
  }

  useEffect(() => {
    const pageTitle = safeTitle ? `${safeTitle} | ${name}` : name;
    document.title = pageTitle;
    
    const setMetaTag = (attr: string, key: string, content: string) => {
      let element = document.querySelector(`meta[${attr}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const removeMetaTag = (attr: string, key: string) => {
      const element = document.querySelector(`meta[${attr}="${key}"]`);
      if (element) element.remove();
    };

    const siteUrl = SITE_URL;

    // Always derive canonical from the current page path — ensures self-referencing
    // canonical on every locale (e.g., /de/blog/foo → https://reviio.app/de/blog/foo)
    let pathname = window.location.pathname;
    // Normalize: remove trailing slash except for root
    if (pathname !== '/' && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }
    const canonicalUrl = `${siteUrl}${pathname}`;
    
    const lang = document.documentElement.lang || 'en';
    const defaultOgImage = `${siteUrl}/og-${lang}.png`;
    const imageUrl = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultOgImage;

    setMetaTag('name', 'description', safeDescription);

    // Robots: noindex for auth/utility pages that shouldn't appear in search
    if (noindex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      removeMetaTag('name', 'robots');
    }
    
    let linkElement = document.querySelector(`link[rel="canonical"]`);
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', canonicalUrl);

    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', safeDescription);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', imageUrl);

    setMetaTag('name', 'twitter:creator', name);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', safeDescription);
    setMetaTag('name', 'twitter:image', imageUrl);

    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptElement);
      }
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Add hreflang links (skip for noindex pages — no point signalling alternates)
    const supportedLangs = ['en', 'es', 'fr', 'de', 'it', 'tr', 'ar', 'bn', 'pt'];

    if (noindex) {
      // Clean up any stale hreflang links from previous page
      supportedLangs.forEach(l => {
        const el = document.querySelector(`link[rel="alternate"][hreflang="${l}"]`);
        if (el) el.remove();
      });
      const xd = document.querySelector(`link[rel="alternate"][hreflang="x-default"]`);
      if (xd) xd.remove();
    } else {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const firstSegment = pathSegments[0];
      const isLangPrefixed = supportedLangs.includes(firstSegment);
      const cleanPath = isLangPrefixed ? '/' + pathSegments.slice(1).join('/') : window.location.pathname;
      
      // Remove trailing slash except for root
      const normalizedCleanPath = cleanPath !== '/' && cleanPath.endsWith('/') ? cleanPath.slice(0, -1) : cleanPath;

      supportedLangs.forEach(l => {
        let hreflangElement = document.querySelector(`link[rel="alternate"][hreflang="${l}"]`);
        if (!hreflangElement) {
          hreflangElement = document.createElement('link');
          hreflangElement.setAttribute('rel', 'alternate');
          hreflangElement.setAttribute('hreflang', l);
          document.head.appendChild(hreflangElement);
        }
        const prefix = l === 'en' ? '' : `/${l}`;
        const fullPath = `${prefix}${normalizedCleanPath === '/' ? '' : normalizedCleanPath}` || '/';
        hreflangElement.setAttribute('href', `${siteUrl}${fullPath}`);
      });

      // Add x-default (points to the English version)
      let xDefaultElement = document.querySelector(`link[rel="alternate"][hreflang="x-default"]`);
      if (!xDefaultElement) {
        xDefaultElement = document.createElement('link');
        xDefaultElement.setAttribute('rel', 'alternate');
        xDefaultElement.setAttribute('hreflang', 'x-default');
        document.head.appendChild(xDefaultElement);
      }
      xDefaultElement.setAttribute('href', `${siteUrl}${normalizedCleanPath}`);
    }

  }, [safeTitle, safeDescription, name, type, image, schema, noindex]);

  return null;
}
